import React, { useState, useEffect, useMemo, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Link,
  Paper,
  Box as MuiBox,
  Badge,
  IconButton,
  Popover,
  TextField,
  Chip,
  GlobalStyles,
  Tooltip as MuiTooltip
} from '@mui/material';
import {
  Link as LinkIcon,
  GitHub as GitHubIcon,
  Description as DocIcon,
  Code as CodeIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AccessTime as AccessTimeIcon,
  Support as SupportIcon,
  CalendarToday as CalendarTodayIcon,
  InsertInvitation as InsertInvitationIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  RadioButtonUnchecked as ZeroHoursIcon,
  Warning as OverBookingIcon,
} from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from '@mui/x-data-grid';
import { API_ENDPOINTS } from '../config/api.config';
import { SUPPORT_CONFIG } from '../config/local.config';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell } from 'recharts';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { UserContext } from '../components/MiniDrawer';
import { getAbsences } from '../utils/absenceService';

const usefulLinks = [
  {
    title: "Documentation",
    description: "Official documentation and guides",
    icon: <DocIcon />,
    links: [
      {
        text: "My Area Guide",
        url: "/guide/RUNTIME-How to_ User Area.pdf"
      },
      {
        text: "Manager Area Guide", 
        url: "/guide/RUNTIME-How to_ Manager Area.pdf"
      }
    ]
  },
  {
    title: "Need Help?",
    description: "Contact our support",
    icon: <SupportIcon />,
    url: `mailto:${SUPPORT_CONFIG.email}?subject=Support Request - RUNtime App - [Brief description of the issue]`
  }
];

export default function Home() {
  const { selectedUser, users, cognitoUser, isAuthenticated } = useContext(UserContext);
  const [date, setDate] = useState(new Date());
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const selectedMonth = date.getMonth();
  const selectedYear = date.getFullYear();
  const navigate = useNavigate();

  const selectedUserObj = users.find(u => String(u.id) === String(selectedUser));
  
  // Priorità: informazioni Cognito, poi utente selezionato dal sistema interno
  const userLabel = cognitoUser && isAuthenticated 
    ? cognitoUser.email || cognitoUser.name || cognitoUser.username
    : selectedUserObj 
      ? selectedUserObj.username || selectedUserObj.name || selectedUserObj.email || selectedUserObj.id 
      : '';

  const [absenceDays, setAbsenceDays] = useState([]);

  useEffect(() => {
    if (!selectedUser || selectedUser === 'ALL') { setAbsenceDays([]); return; }
    const yearMonth = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
    getAbsences(selectedUser, yearMonth).then(setAbsenceDays).catch(() => setAbsenceDays([]));
  }, [selectedUser, selectedYear, selectedMonth]);

  // Handler per navigare a monthly-user-view con filtro mese corrente
  const handleNavigateToMonthlyView = () => {
    const month = selectedMonth + 1; // Converte da 0-based a 1-based
    const year = selectedYear;
    navigate(`/monthly-user-view?month=${month}&year=${year}`);
  };

  // Helpers per giorni del mese
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
  const firstDayOfMonth = getFirstDayOfMonth(selectedYear, selectedMonth);

  const handlePrevMonth = () => {
    setDate(new Date(selectedYear, selectedMonth - 1, 1));
  };

  const handleNextMonth = () => {
    setDate(new Date(selectedYear, selectedMonth + 1, 1));
  };

  const handleDayClick = (event, day) => {
    const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const hours = (hoursData[dateStr] || 0) / 60;
    const completion = Math.round((hours / 8) * 100);
    setSelectedDay({ day, hours, completion });
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedDay(null);
  };

  const open = Boolean(anchorEl);

  const formatDate = (day) => {
    return `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const renderCalendarHeader = () => {
    const dayNames = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
    return (
      <Grid container spacing={2} sx={{ mb: 0, pb: 1 }}>
        {dayNames.map(day => (
          <Grid item xs key={day}>
            <Typography variant="body2" align="center" sx={{ color: '#607c89', fontWeight: 'bold' }}>
              {day}
            </Typography>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderCalendarDays = () => {
    const weeks = [];
    let days = [];
    let offset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    let dayCounter = 1;
    const totalCells = Math.ceil((offset + daysInMonth) / 7) * 7;

    for (let cell = 0; cell < totalCells; cell++) {
      if (cell < offset || dayCounter > daysInMonth) {
        days.push(
          <Grid item xs key={`empty-${cell}`} />
        );
      } else {
        const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(dayCounter).padStart(2, '0')}`;
        const hours = hoursData[dateStr] ? hoursData[dateStr] / 60 : undefined;
        const isOk = hours !== undefined && hours >= 8;
        days.push(
          <Grid item xs key={dayCounter}>
            <MuiBox 
              onClick={(e) => handleDayClick(e, dayCounter)}
              sx={{
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                px: 1,
                bgcolor: 'white',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {String(dayCounter).padStart(2, '0')}
              </Typography>
              {isOk ? (
                <CheckIcon sx={{ color: '#5ab031', fontSize: 18 }} />
              ) : (
                <CloseIcon sx={{ color: '#e53935', fontSize: 18 }} />
              )}
            </MuiBox>
          </Grid>
        );
        dayCounter++;
      }
      if ((cell + 1) % 7 === 0) {
        weeks.push(
          <Grid container spacing={2} key={`week-${cell / 7}`} sx={{ mb: 2 }}>{days}</Grid>
        );
        days = [];
      }
    }
    return weeks;
  };

  // Stato per i worklog e loading
  const [worklogs, setWorklogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Calcola inizio e fine mese in base al mese selezionato nel calendario
  const startOfMonth = new Date(selectedYear, selectedMonth, 1);
  const endOfMonth = new Date(selectedYear, selectedMonth + 1, 0);
  const startDateStr = `${startOfMonth.getFullYear()}-${String(startOfMonth.getMonth() + 1).padStart(2, '0')}-01`;
  const endDateStr = `${endOfMonth.getFullYear()}-${String(endOfMonth.getMonth() + 1).padStart(2, '0')}-${String(endOfMonth.getDate()).padStart(2, '0')}`;

  // Fetch worklogs del mese selezionato
  useEffect(() => {
    if (!selectedUser || selectedUser === '') return;
    setLoading(true);
    setError(null);
    const url = selectedUser === 'ALL'
      ? `${API_ENDPOINTS.WORKLOGS}?start_date=${startDateStr}&end_date=${endDateStr}`
      : `${API_ENDPOINTS.WORKLOGS}?start_date=${startDateStr}&end_date=${endDateStr}&user_id=${selectedUser}`;
    fetch(url)
      .then(res => res.json())
      .then(data => {
        const arr = data.worklogs || [];
        setWorklogs(arr);
      })
      .catch(err => {
        setError('Errore nel recupero dei worklog');
        setWorklogs([]);
      })
      .finally(() => setLoading(false));
  }, [startDateStr, endDateStr, selectedUser]);

  // Mappa giorno -> effort per il calendario
  const hoursData = useMemo(() => {
    const map = {};
    worklogs.forEach(wl => {
      if (wl.date && wl.effort) {
        // Prendi solo la parte YYYY-MM-DD
        const dateKey = typeof wl.date === 'string' ? wl.date.slice(0, 10) : wl.date;
        map[dateKey] = (map[dateKey] || 0) + Number(wl.effort);
      }
    });
    return map;
  }, [worklogs]);

  // Stato per i giorni con worklog (summary API)
  const [worklogSummary, setWorklogSummary] = useState([]);

  // Fetch dei giorni con worklog tramite /user_effort_summary
  useEffect(() => {
    if (!selectedUser || selectedUser === '') return;
    const yearMonth = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
    fetch(`${API_ENDPOINTS.USER_EFFORT_SUMMARY}?user_id=${selectedUser}&year_month=${yearMonth}`)
      .then(res => res.json())
      .then(data => {
        // data.data: [ { work_date, total_effort, ... } ]
        setWorklogSummary(Array.isArray(data.data) ? data.data : []);
      })
      .catch(() => setWorklogSummary([]));
  }, [selectedUser, selectedYear, selectedMonth]);

  // Calcola il totale ore del mese come somma dei total_effort di user_effort_summary (convertito da minuti a ore)
  const displayedHours = useMemo(
    () => Math.round(worklogSummary.reduce((sum, d) => sum + ((Number(d.total_effort) || 0) / 60), 0) * 100) / 100,
    [worklogSummary]
  );
  const theoreticalHours = daysInMonth * 8;
  const percent = Math.round((displayedHours / theoreticalHours) * 100);

  // Calcola statistiche giornaliere per i box
  const dailyStats = useMemo(() => {
    let days8h = 0;        // Giorni con 8+ ore
    let daysUnder8h = 0;   // Giorni con 0-8 ore
    let daysZero = 0;      // Giorni con 0 ore (solo feriali)
    let daysOver8h = 0;    // Giorni con più di 8 ore (overbooking)

    // Scorre tutti i giorni del mese
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayOfWeek = new Date(selectedYear, selectedMonth, day).getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // 0=domenica, 6=sabato
      
      const dayData = worklogSummary.find(item => {
        const d = new Date(item.work_date);
        const itemDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        return itemDate === dateStr;
      });
      
      const totalHours = dayData ? Math.round(((Number(dayData.total_effort) || 0) / 60) * 100) / 100 : 0;
      
      if (totalHours > 8) {
        daysOver8h++;
      } else if (totalHours === 8) {
        days8h++;
      } else if (totalHours > 0) {
        daysUnder8h++;
      } else if (!isWeekend) {
        // Solo giorni feriali con 0 ore
        daysZero++;
      }
    }

    return { days8h, daysUnder8h, daysZero, daysOver8h };
  }, [worklogSummary, daysInMonth, selectedYear, selectedMonth]);

  // Calcola i giorni lavorativi del mese (escludendo sabato e domenica, eccetto se hanno worklog)
  const workingDaysInMonth = useMemo(() => {
    let workingDays = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(selectedYear, selectedMonth, day);
      const dayOfWeek = date.getDay(); // 0 = domenica, 6 = sabato
      const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        // Giorni feriali: sempre inclusi
        workingDays++;
      } else {
        // Weekend: inclusi solo se hanno worklog
        const hasWorklog = worklogSummary.find(item => {
          const d = new Date(item.work_date);
          const itemDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          return itemDate === dateStr && Number(item.total_effort) > 0;
        });
        if (hasWorklog) {
          workingDays++;
        }
      }
    }
    return workingDays;
  }, [daysInMonth, selectedYear, selectedMonth, worklogSummary]);

  // Stato per la lista progetti e totali ore per progetto
  const [projectEffort, setProjectEffort] = useState([]);

  // Fetch dei totali progetto/mese/utente tramite monthly_worklog_summary_user_project
  useEffect(() => {
    if (!selectedUser || selectedUser === '') return;
    const yearMonth = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
    fetch(`${API_ENDPOINTS.MONTHLY_WORKLOG_SUMMARY_USER_PROJECT}?user_id=${selectedUser}&year_month=${yearMonth}`)
      .then(res => res.json())
      .then(data => {
        // Raggruppa per project_name e somma total_effort
        const map = {};
        if (Array.isArray(data.data)) {
          data.data.forEach(item => {
            const name = item.project_name || 'N/A';
            map[name] = (map[name] || 0) + (Number(item.total_effort) || 0);
          });
        }
        setProjectEffort(Object.entries(map).map(([project_name, total_effort]) => ({ project_name, total_effort })));
      })
      .catch(() => setProjectEffort([]));
  }, [selectedUser, selectedYear, selectedMonth]);

  const projectColumns = [
    { field: 'project_name', headerName: 'Project', width: 140 },
    { field: 'effort', headerName: 'Tot. Monthly effort', width: 120 },
  ];

  // Funzione per redirect su daily-tracking con data selezionata
  const handleCalendarDayClick = (day) => {
    if (!day) return;
    // Costruisci la data in locale per evitare problemi di fuso orario
    const year = day.getFullYear();
    const month = String(day.getMonth() + 1).padStart(2, '0');
    const date = String(day.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${date}`;
    navigate(`/daily-tracking?date=${dateStr}`);
  };

  // Funzione di utilità per sapere se c'è worklog in un certo giorno
  function hasWorklogForDay(dateStr) {
    return !!worklogSummary.find(item => {
      // Normalizza la data a yyyy-mm-dd
      const d = new Date(item.work_date);
      const itemDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return itemDate === dateStr && Number(item.total_effort) > 0;
    });
  }

  const [expanded, setExpanded] = useState([false, false, false, false]);
  const handleExpandClick = idx => {
    setExpanded(expanded => expanded.map((v, i) => i === idx ? !v : v));
  };

  // Mostra messaggio se non c'è un utente selezionato
  if (!selectedUser || selectedUser === '') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
        <Typography variant="h5" sx={{ mb: 2, color: 'text.secondary' }}>
          No user selected
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center' }}>
          Waiting for authentication to load...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <GlobalStyles styles={{
        '.css-v0ohlj': {
          width: 'auto !important'
        },
        '.PrivatePickersSlideTransition-root.MuiDayPicker-slideTransition': {
          minHeight: '260px !important',
        },
      }} />
      <Box sx={{ pb: 5, pt: 0 }}>
        <Box sx={{ 
          width: '100%',
          margin: '0 auto',
        }}>
          <Typography variant="h4" sx={{ mb: 4, color: '#607c89', fontSize: '1.2rem', textAlign: 'center', fontWeight: 'bold' }}>
            {cognitoUser && isAuthenticated 
              ? `Welcome back, ${cognitoUser.email}!` 
              : userLabel 
                ? `Hi ${userLabel}!` 
                : 'Welcome!'
            }
          </Typography>

          {/* Monthly Progress and Calendar Section */}
          <Paper sx={{
            p: { xs: 1, md: 1 },
            mb: 4,
            bgcolor: 'transparent',
            maxWidth: '100vw',
            margin: 0,
            pb: 10, // padding bottom extra
            boxShadow: 'none',
          }}>
            <Typography variant="h5" sx={{ color: '#607c89', mb: 3, textAlign: 'center' }}>
              Recap monthly tracking for{' '}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {new Date(startDateStr).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </Box>
            </Typography>

            {/* Daily Statistics Section - Temporarily hidden */}
            <Grid container spacing={3} justifyContent="center" sx={{ mb: 4, mt: 2, display: 'none' }}>
                           {/* Box 1: Giorni con 8+ ore */}
             <Grid item xs={12} sm={6} md={3}>
               <Card 
                 onClick={handleNavigateToMonthlyView}
                 sx={{ 
                   height: 140,
                   display: 'flex',
                   flexDirection: 'column',
                   alignItems: 'center',
                   justifyContent: 'center',
                   position: 'relative',
                   overflow: 'hidden',
                   cursor: 'pointer',
                   '&:hover': {
                     boxShadow: 6,
                     transform: 'translateY(-2px)',
                     transition: 'all 0.3s'
                   }
                 }}
               >
                  <CardContent sx={{ textAlign: 'center', width: '100%', position: 'relative', zIndex: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 32, color: '#5ab031', mr: 1 }} />
                      <Typography variant="h3" sx={{ color: '#5ab031', fontWeight: 'bold' }}>
                        {dailyStats.days8h}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Days with 8 hours
                    </Typography>
                  </CardContent>
                  <Box sx={{ 
                    position: 'absolute', 
                    top: -20, 
                    right: -20, 
                    opacity: 0.1, 
                    transform: 'rotate(15deg)' 
                  }}>
                    <CheckCircleIcon sx={{ fontSize: 80, color: '#5ab031' }} />
                  </Box>
                </Card>
              </Grid>

                           {/* Box 2: Giorni con meno di 8 ore */}
             <Grid item xs={12} sm={6} md={3}>
               <Card 
                 onClick={handleNavigateToMonthlyView}
                 sx={{ 
                   height: 140,
                   display: 'flex',
                   flexDirection: 'column',
                   alignItems: 'center',
                   justifyContent: 'center',
                   position: 'relative',
                   overflow: 'hidden',
                   cursor: 'pointer',
                   '&:hover': {
                     boxShadow: 6,
                     transform: 'translateY(-2px)',
                     transition: 'all 0.3s'
                   }
                 }}
               >
                  <CardContent sx={{ textAlign: 'center', width: '100%', position: 'relative', zIndex: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                      <ScheduleIcon sx={{ fontSize: 32, color: '#47b1a3', mr: 1 }} />
                      <Typography variant="h3" sx={{ color: '#47b1a3', fontWeight: 'bold' }}>
                        {dailyStats.daysUnder8h}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Days under 8 hours
                    </Typography>
                  </CardContent>
                  <Box sx={{ 
                    position: 'absolute', 
                    top: -20, 
                    right: -20, 
                    opacity: 0.1, 
                    transform: 'rotate(15deg)' 
                  }}>
                    <ScheduleIcon sx={{ fontSize: 80, color: '#47b1a3' }} />
                  </Box>
                </Card>
              </Grid>

                           {/* Box 3: Giorni con 0 ore */}
             <Grid item xs={12} sm={6} md={3}>
               <Card 
                 onClick={handleNavigateToMonthlyView}
                 sx={{ 
                   height: 140,
                   display: 'flex',
                   flexDirection: 'column',
                   alignItems: 'center',
                   justifyContent: 'center',
                   position: 'relative',
                   overflow: 'hidden',
                   cursor: 'pointer',
                   '&:hover': {
                     boxShadow: 6,
                     transform: 'translateY(-2px)',
                     transition: 'all 0.3s'
                   }
                 }}
               >
                  <CardContent sx={{ textAlign: 'center', width: '100%', position: 'relative', zIndex: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                      <ZeroHoursIcon sx={{ fontSize: 32, color: '#f25757', mr: 1 }} />
                      <Typography variant="h3" sx={{ color: '#f25757', fontWeight: 'bold' }}>
                        {dailyStats.daysZero}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Days with no hours
                    </Typography>
                  </CardContent>
                  <Box sx={{ 
                    position: 'absolute', 
                    top: -20, 
                    right: -20, 
                    opacity: 0.1, 
                    transform: 'rotate(15deg)' 
                  }}>
                    <ZeroHoursIcon sx={{ fontSize: 80, color: '#f25757' }} />
                  </Box>
                </Card>
              </Grid>

                           {/* Box 4: Giorni in overbooking */}
             <Grid item xs={12} sm={6} md={3}>
               <Card 
                 onClick={handleNavigateToMonthlyView}
                 sx={{ 
                   height: 140,
                   display: 'flex',
                   flexDirection: 'column',
                   alignItems: 'center',
                   justifyContent: 'center',
                   position: 'relative',
                   overflow: 'hidden',
                   cursor: 'pointer',
                   '&:hover': {
                     boxShadow: 6,
                     transform: 'translateY(-2px)',
                     transition: 'all 0.3s'
                   }
                 }}
               >
                  <CardContent sx={{ textAlign: 'center', width: '100%', position: 'relative', zIndex: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                      <OverBookingIcon sx={{ fontSize: 32, color: '#9e9e9e', mr: 1 }} />
                      <Typography variant="h3" sx={{ color: '#9e9e9e', fontWeight: 'bold' }}>
                        {dailyStats.daysOver8h}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Days with +8 hours
                    </Typography>
                  </CardContent>
                  <Box sx={{ 
                    position: 'absolute', 
                    top: -20, 
                    right: -20, 
                    opacity: 0.1, 
                    transform: 'rotate(15deg)' 
                  }}>
                    <OverBookingIcon sx={{ fontSize: 80, color: '#9e9e9e' }} />
                  </Box>
                </Card>
              </Grid>
            </Grid>
            
            
            <Grid container spacing={2} alignItems="stretch" sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' }, minHeight: 0 }}>
              {/* Colonna sinistra: ore totali sopra, lista progetti sotto */}
              <Grid item xs={12} md={7} lg={8} sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
                <MuiBox sx={{
                  width: '100%',
                  minWidth: 0,
                  maxWidth: { xs: '100%', sm: 700, md: '80%', lg: '100%' },
                  flexGrow: 1,
                  margin: '0 auto',
                  mb: 3,
                  gap: 2,
                  overflow: 'hidden',
                }}>
                  {/* Pannello ore totali */}
                  <Box
                    sx={{
                      background: '#5ab031',
                      borderRadius: 2,
                      boxSizing: 'border-box',
                      p: { xs: 1.5, sm: 3 },
                      mb: 2,
                      boxShadow: 1,
                      width: '100%',
                      maxWidth: { xs: '100%', sm: 700, md: '85%', lg: '100%' },
                      ml: 0,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: 'stretch',
                      justifyContent: 'center',
                      gap: 2,
                      overflow: 'hidden',
                    }}
                  >
                    {/* Box 1: Ore totali */}
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
                      <Box>
                        <Typography sx={{ color: 'white', fontSize: { xs: 32, md: 36, lg: 48 }, fontWeight: 700, lineHeight: 1 }}>
                          {displayedHours} h
                        </Typography>
                        <Typography sx={{ color: 'white', fontSize: { xs: 12, md: 13, lg: 14 }, opacity: 0.9 }}>
                          Total hours tracked
                        </Typography>
                      </Box>
                      <Box sx={{ opacity: 0.25, fontSize: { xs: 40, md: 48, lg: 64 }, ml: 2 }}>
                        <AccessTimeIcon sx={{ fontSize: { xs: 40, md: 48, lg: 64 }, color: 'white' }} />
                      </Box>
                    </Box>
                    {/* Box 2: Giorni con worklog su giorni totali */}
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0, borderLeft: { sm: '1px solid rgba(255,255,255,0.18)' }, pl: { sm: 3 }, mt: { xs: 2, sm: 0 } }}>
                      <Box>
                        <Typography sx={{ color: 'white', fontSize: { xs: 28, md: 30, lg: 43 }, fontWeight: 700, lineHeight: 1 }}>
                          {/* Calcolo giorni con worklog su giorni lavorativi (escludendo weekend) */}
                          {worklogSummary.filter(d => Number(d.total_effort) > 0).length} / {workingDaysInMonth}
                        </Typography>
                        <Typography sx={{ color: 'white', fontSize: { xs: 12, md: 13, lg: 14 }, opacity: 0.9 }}>
                        Total Days with hours
                        </Typography>
                      </Box>
                      <Box sx={{ opacity: 0.25, fontSize: { xs: 40, md: 48, lg: 64 }, ml: 2 }}>
                        <InsertInvitationIcon sx={{ fontSize: { xs: 40, md: 48, lg: 64 }, color: 'white' }} />
                      </Box>
                    </Box>
                  </Box>
                  {/* Lista progetti sotto il totale ore */}
                  <Box
                    sx={{
                      background: 'white',
                      borderRadius: 2,
                      boxSizing: 'border-box',
                      p: { xs: 1.5, sm: 3 },
                      boxShadow: 1,
                      width: '100%',
                      maxWidth: { xs: '100%', sm: 700, md: '85%', lg: '100%' },
                      minWidth: 0,
                      ml: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      overflowX: 'auto',
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ mt: 1 }}>
                      {projectEffort.length === 0 ? (
                        <Typography sx={{ color: '#607c89', opacity: 0.7, fontSize: 15 }}>
                          No hours tracked
                        </Typography>
                      ) : (
                        (() => {
                                                     const maxEffort = Math.max(...projectEffort.map(row => (Number(row.total_effort) || 0) / 60), 1);
                          return projectEffort.map((row) => (
                            <Box key={row.project_name} sx={{ display: 'flex', alignItems: 'center', mb: 1, minHeight: 28 }}>
                              <Box sx={{ minWidth: 0, flex: '0 0 160px', color: '#374151', fontSize: 15, pr: 2, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', textAlign: 'right', fontFamily: 'Roboto, sans-serif' }}>
                                {row.project_name}
                              </Box>
                              <Box sx={{ flex: 1, mx: 1, height: 8, bgcolor: 'rgba(90,176,49,0.08)', borderRadius: 2, position: 'relative', minWidth: 30 }}>
                                                                 <Box sx={{
                                   width: `${((Number(row.total_effort) / 60) / maxEffort) * 100}%`,
                                  height: '100%',
                                  bgcolor: 'rgba(90,176,49,0.45)',
                                  borderRadius: 2,
                                  transition: 'width 0.4s',
                                }} />
                              </Box>
                                                             <Box sx={{ minWidth: 36, color: '#374151', fontWeight: 700, fontSize: 16, pl: 1, textAlign: 'right', fontFamily: 'Roboto, sans-serif' }}>
                                 {Math.round((Number(row.total_effort) / 60) * 100) / 100} h
                               </Box>
                            </Box>
                          ));
                        })()
                      )}
                    </Box>
                  </Box>
                </MuiBox>
              </Grid>
              {/* Colonna destra: calendario invariato */}
              <Grid item xs={12} md={5} lg={4} sx={{ display: 'flex', justifyContent: 'center', flexGrow: 1, minWidth: 0 }}>
                <MuiBox sx={{
                  bgcolor: 'white',
                  borderRadius: 2,
                  p: 2,
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  width: '100%',
                  maxWidth: '320px',
                  mb: 2,
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  overflow: 'visible',
                  flexShrink: 0,
                  flexGrow: 0,
                  minHeight: '304px', // 264+40
                }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <StaticDatePicker
                      displayStaticWrapperAs="desktop"
                      value={date}
                      onChange={setDate}
                      showDaysOutsideCurrentMonth
                      onMonthChange={setDate}
                      renderInput={(params) => <TextField {...params} sx={{ width: 120 }} />}
                      renderDay={(day, _value, DayProps) => {
                        const dateStr = day
                          ? `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`
                          : '';
                        const isCurrentMonth = day && day.getMonth() === selectedMonth && day.getFullYear() === selectedYear;
                        const totalHours = Math.round(((hoursData[dateStr] || 0) / 60) * 100) / 100;
                        const absenceEntry = isCurrentMonth ? absenceDays.find(v => v.date === dateStr) : null;
                        const hasAbsence = !!absenceEntry;
                        let dotColor = null;
                        if (totalHours > 8) dotColor = '#f25757';
                        else if (totalHours > 0 && totalHours <= 8) dotColor = '#47b1a3';
                        else dotColor = 'transparent';
                        const tooltipTitle = hasAbsence
                          ? `Absence (${absenceEntry.full_day ? 'full day' : 'partial'})${totalHours > 0 ? ` · ${totalHours}h` : ''}`
                          : `Total hours: ${totalHours}`;
                        return (
                          <MuiTooltip title={tooltipTitle} arrow>
                            <Box sx={{ position: 'relative', width: '100%', height: 40 }}>
                              <PickersDay
                                {...DayProps}
                                day={day}
                                onClick={() => handleCalendarDayClick(day)}
                                sx={{
                                  width: 36,
                                  height: 36,
                                  ...(DayProps.selected && {
                                    backgroundColor: '#248b7e !important',
                                    color: 'white !important',
                                    '&:hover': {
                                      backgroundColor: '#248b7e !important',
                                      opacity: 0.9,
                                    },
                                  }),
                                  ...(day && new Date().toDateString() === day.toDateString() && !DayProps.selected && {
                                    border: '1.2px solid #248b7e !important',
                                    color: '#248b7e !important',
                                    fontWeight: 700,
                                    backgroundColor: 'white !important',
                                  })
                                }}
                              />
                              {isCurrentMonth && dotColor && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    left: hasAbsence ? 4 : 0,
                                    right: hasAbsence ? 'auto' : 0,
                                    margin: hasAbsence ? 0 : 'auto',
                                    bottom: 2,
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: dotColor,
                                    pointerEvents: 'none',
                                  }}
                                />
                              )}
                              {hasAbsence && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    right: 4,
                                    bottom: 2,
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: absenceEntry.full_day ? '#e53935' : '#f9a825',
                                    pointerEvents: 'none',
                                  }}
                                />
                              )}
                            </Box>
                          </MuiTooltip>
                        );
                      }}
                    />
                  </LocalizationProvider>
                </MuiBox>
              </Grid>
            </Grid>
          </Paper>

          {/* Useful Links Section */}
          <Typography variant="h5" sx={{ mb: 2, color: '#607c89', mt: 3, textAlign: 'center' }}>
            Useful Links
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {usefulLinks.map((link) => (
              <Grid item xs={12} sm={6} md={4} key={link.title}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)',
                      transition: 'all 0.3s'
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', width: '100%' }}>
                    <Box sx={{ color: '#5ab031', mb: 1, textAlign: 'center' }}>
                      {link.icon}
                    </Box>
                    <Typography variant="h6" component="div" sx={{ mb: 1, textAlign: 'center' }}>
                      {link.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                      {link.description}
                    </Typography>
                    {link.links ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                        {link.links.map((subLink, index) => (
                          <Link 
                            key={index}
                            href={subLink.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            sx={{ 
                              color: '#5ab031',
                              textDecoration: 'none',
                              textAlign: 'center',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {subLink.text} →
                          </Link>
                        ))}
                      </Box>
                    ) : (
                      <Link 
                        href={link.url} 
                        target={link.url.startsWith('mailto:') ? '_self' : '_blank'} 
                        rel={link.url.startsWith('mailto:') ? '' : 'noopener noreferrer'}
                        sx={{ 
                          color: '#5ab031',
                          textDecoration: 'none',
                          textAlign: 'center',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {link.url.startsWith('mailto:') ? 'Write to support →' : 'Visit Link →'}
                      </Link>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </>
  );
}